/**
 * Plain-language rejection messages (FRT-024).
 * Internal codes stay in logs and as a small support reference — customers
 * see what happened and what to do next, on whichever device they're holding.
 * Fraud-class reasons are deliberately vague: never coach document forgery.
 */
export type FriendlyRejection = {
  title: string;
  message: string;
  action: string;
};

export function friendlyRejection(reason?: string | null): FriendlyRejection {
  switch ((reason || '').toUpperCase()) {
    case 'NOT_AN_ID_DOCUMENT':
      return {
        title: "That doesn't look like an ID",
        message: 'The image you submitted is not an identity document.',
        action:
          'Photograph the front of your government-issued ID — National ID, driver’s license, or passport.',
      };
    case 'SCREEN_REPLICA':
      return {
        title: 'Scan the physical card',
        message:
          'This looks like a photo of a screen or a paper copy, which banks cannot accept.',
        action:
          'Place your original ID card on a flat surface and photograph it directly with your camera.',
      };
    case 'FRONT_OCR_FAILED':
    case 'FRONT_LOW_CONFIDENCE':
      return {
        title: "We couldn't read your ID",
        message: 'The photo was not clear enough to read.',
        action:
          'Retake it in good lighting, hold the camera steady, and make sure the whole card fills the frame with no glare.',
      };
    case 'BACK_BARCODE_NOT_FOUND':
      return {
        title: "We couldn't read the back of your ID",
        message: 'The barcode on the back was missing or unreadable.',
        action:
          'Rescan the back side flat on a dark surface, with the barcode fully visible and no fingers covering it.',
      };
    case 'BACK_MRZ_CHECKSUM_FAILED':
    case 'BACK_MRZ_BARCODE_MISMATCH':
      return {
        title: "We couldn't confirm the back of your document",
        message: 'The machine-readable area did not check out.',
        action: 'Rescan the back side clearly, or try a different supported ID.',
      };
    case 'CROSS_VALIDATION_FAILED':
      return {
        title: 'The two sides don’t match',
        message: 'The front and back don’t appear to be from the same document.',
        action: 'Rescan both sides of the same ID, front first, then back.',
      };
    case 'DOCUMENT_EXPIRED':
      return {
        title: 'This document has expired',
        message: 'The expiry date on the document is in the past.',
        action: 'Use a valid, non-expired ID.',
      };
    case 'LIVENESS_FAILED':
      return {
        title: "We couldn't confirm it was really you",
        message: 'The live face check did not complete.',
        action:
          'Try again in a well-lit place, face the camera directly, and follow the on-screen movement prompts.',
      };
    case 'FACE_NOT_DETECTED':
      return {
        title: "We couldn't see your face",
        message: 'No face was detected during the check.',
        action:
          'Make sure your face is centred, uncovered, and well lit, then try again.',
      };
    case 'FACE_MATCH_FAILED':
      return {
        title: 'The selfie didn’t match the ID photo',
        message: 'Your live photo did not match the photo on the document.',
        action:
          'Remove hats or glasses, use good lighting, and make sure you are using your own ID.',
      };
    default:
      // Includes tamper/deepfake/AML classes on purpose — no coaching.
      return {
        title: 'Verification unsuccessful',
        message: 'We could not verify your identity automatically.',
        action:
          'You can try again, or contact support and a member of our team will review it manually.',
      };
  }
}
